//  ----------      START   IMPORT  BLOCK       ----------
import { useEffect } from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { useSearchParams } from 'react-router-dom';

//  Widgets & Components
import FloatingFilterBar from './FloatingFilterBar';
import MapWidget from './MapWidget';
import CalendarWidget from './CalendarWidget';
import MedicCard from './MedicCard';
import ServerPagination from './ServerPagination';

//  Hooks
import { useMedicSearch } from '../../API/Hooks/useMedicSearch';
//  ----------      END   IMPORT  BLOCK       ----------

const RESULTS_PER_PAGE = 10;

const ResultsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Extract params from URL
  const queryParam = searchParams.get('query') || '';
  const specialityParam = searchParams.get('speciality') || '';
  const pageParam = parseInt(searchParams.get('page') || '1', 10);

  const { 
    results, 
    total, 
    loading, 
    selectedMedic, 
    setSelectedMedic, 
    executeSearch 
  } = useMedicSearch();

  // Execute search when URL parameters or page change
  useEffect(() => {
    const skip = (pageParam - 1) * RESULTS_PER_PAGE;
    
    executeSearch({
      query: queryParam,
      speciality: specialityParam,
      skip: skip,
      limit: RESULTS_PER_PAGE,
    });
  }, [queryParam, specialityParam, pageParam, executeSearch]);

  const handlePageChange = (newPage) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('page', newPage.toString());
    setSearchParams(newParams);
  };

  const handleManualSearch = (newQuery) => {
    const newParams = new URLSearchParams(searchParams);
    if (newQuery) {
      newParams.set('query', newQuery);
    } else {
      newParams.delete('query');
    }
    newParams.set('page', '1');
    setSearchParams(newParams);
  };

  // GeoJSON standard: coordinates = [longitude, latitude]
  const coordinates = selectedMedic?.address?.location?.coordinates;
  const lng = coordinates ? coordinates[0] : 0;
  const lat = coordinates ? coordinates[1] : 0;

  return (
    <Container fluid className="mt-2">
      <Row>
        <Col md={5} style={{ height: 'calc(100vh - 60px)', overflow: 'hidden' }}>
          <div className="h-100 d-flex flex-column">
            <FloatingFilterBar 
              onSearch={handleManualSearch}
              initialQuery={queryParam}
            />
            
            <div className="flex-grow-1 overflow-auto px-3">
              {loading ? (
                <p className="mt-3">Loading medics...</p>
              ) : results.length === 0 ? (
                <h5 className="mt-3">No medics found matching your criteria.</h5>
              ) : (
                results.map((user) => (
                  <MedicCard 
                    key={user.username} 
                    user={user} 
                    isSelected={selectedMedic?.username === user.username}
                    onSelect={() => setSelectedMedic(user)}
                  />
                ))
              )}

              {total > RESULTS_PER_PAGE && (
                <ServerPagination 
                  total={total} 
                  limit={RESULTS_PER_PAGE} 
                  current={pageParam} 
                  onChange={handlePageChange} 
                />
              )}
            </div>
          </div>
        </Col>

        <Col md={7}>
          <CalendarWidget profile={selectedMedic} />
          <MapWidget lat={lat} lng={lng} />
        </Col>
      </Row>
    </Container>
  );
};

export default ResultsPage;